
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_700Bold } from '@expo-google-fonts/inter';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

interface Emotion {
  id: string;
  emoji: string;
  label: string;
}

export default function DiarioSentimentoScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  
  const emotions: Emotion[] = [
    { id: 'feliz', emoji: '😀', label: t('diary.emotions.feliz') },
    { id: 'animado', emoji: '🤩', label: t('diary.emotions.animado') },
    { id: 'tranquilo', emoji: '😌', label: t('diary.emotions.tranquilo') },
    { id: 'cansado', emoji: '😴', label: t('diary.emotions.cansado') },
    { id: 'frustrado', emoji: '😣', label: t('diary.emotions.frustrado') },
    { id: 'triste', emoji: '😭', label: t('diary.emotions.triste') },
    { id: 'estressado', emoji: '😤', label: t('diary.emotions.estressado') },
    { id: 'irritado', emoji: '😠', label: t('diary.emotions.irritado') },
    { id: 'ansioso', emoji: '😰', label: t('diary.emotions.ansioso') },
  ];
  const { selectedDate } = useLocalSearchParams<{ selectedDate?: string }>();
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>('feliz'); // Default to 'feliz' as shown in design

  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
    'Sentient-Regular': require('../assets/fonts/Sentient-Regular.otf'),
  });

  if (!fontsLoaded) {
    return null;
  }

  const handleEmotionSelect = (emotionId: string) => {
    setSelectedEmotion(emotionId);
  };

  const handleContinue = () => {
    if (selectedEmotion) {
      // Navigate to the category selection screen
      router.push({
        pathname: '/diario-sentimento-categoria',
        params: { 
          emotion: selectedEmotion,
          selectedDate: selectedDate || new Date().toISOString().split('T')[0]
        }
      });
    }
  };

  const handleBack = () => {
    router.back();
  };

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader 
        title={t('diary.sentiment.title')} 
        onBack={handleBack}
      />

      {/* Body */}
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.body}>
        <Text style={styles.questionText}>{t('diary.sentiment.question')}</Text>
        
        <View style={styles.emotionsGrid}>
          {emotions.map((emotion) => (
            <TouchableOpacity
              key={emotion.id}
              style={[
                styles.emotionCard,
                selectedEmotion === emotion.id && styles.emotionCardSelected
              ]}
              onPress={() => handleEmotionSelect(emotion.id)}
            >
              <Text style={styles.emotionEmoji}>{emotion.emoji}</Text>
              <Text style={styles.emotionLabel}>{emotion.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {/* Button Section */}
      <View style={styles.buttonSection}>
        <PrimaryButton
          title={t('diary.sentiment.nextButton')}
          onPress={handleContinue}
          disabled={!selectedEmotion}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  body: {
    padding: 24,
    gap: 24,
  },
  questionText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8, // 120% of 24px
  },
  emotionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 20,
    minHeight: 340,
  },
  emotionCard: {
    width: 100,
    height: 100,
    padding: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
  },
  emotionCardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  emotionEmoji: {
    fontSize: 32,
    lineHeight: 44.8, // 140% of 32px
  },
  emotionLabel: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6, // 140% of 14px
  },
  buttonSection: {
    padding: 24,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
